const pickFirst = (...values) => values.find((value) => (
  typeof value === 'string' ? value.trim().length > 0 : value != null
)) ?? null;

const isNonEmptyObject = (value) => Boolean(
  value && typeof value === 'object' && Object.keys(value).length > 0
);

const normalizeImageAssetBag = (source = {}) => (
  source.image_assets || source.imageAssets || {}
);

/**
 * Collapse the many image-reference shapes the frontend currently receives
 * into the canonical `{ id, url, path, prompt }` contract (see the backend
 * `ImageReference` model).
 *
 * The wide alias lists below are intentional but transitional: while the
 * BOU-1301 image-ID migration is in flight, the same logical reference still
 * arrives as a flat `portrait_url`/`portrait_path` pair, an `image_assets`
 * portrait object, or a raw image record carrying `image_id` and
 * `proxy_url`/`cdn_url`-style URLs. Once every producer emits the canonical
 * shape, these fallbacks should shrink to just `{ id, url, path, prompt }`.
 * That cleanup is tracked by BOU-1307 (retire legacy image URL/path fallbacks).
 */
export const normalizeImageReference = (source = {}) => {
  const id = pickFirst(
    source.id,
    source.image_id,
  );
  const url = pickFirst(
    source.url,
    source.image_url,
    source.imageUrl,
    source.portrait_url,
    source.portraitUrl,
    source.proxy_url,
    source.proxyUrl,
  );
  const path = pickFirst(
    source.path,
    source.local_path,
    source.localPath,
    source.storage_path,
    source.storagePath,
    source.portrait_path,
    source.portraitPath,
  );
  const prompt = pickFirst(
    source.prompt,
    source.prompt_used,
    source.promptUsed,
    source.portrait_prompt,
    source.portraitPrompt,
  );

  return Object.fromEntries(
    Object.entries({ id, url, path, prompt }).filter(([, value]) => value != null)
  );
};

/**
 * Read the writable portrait image id from a character-like source.
 *
 * The canonical home is `source.image_assets.portrait.id`. The tavern draft
 * still carries the id as a flat `portrait_image_id` (transitional — tracked by
 * BOU-1307); reading it here keeps that knowledge in this ONE helper instead of
 * forcing every consuming component to hydrate the draft itself.
 */
const portraitImageIdForWrite = (source = {}) => {
  const portrait = normalizeImageReference(normalizeImageAssetBag(source).portrait || {});
  return portrait.id
    || pickFirst(source.portrait_image_id, source.portraitImageId)
    || null;
};

/**
 * ID-only character image write contract.
 *
 * Emits `{ image_assets: { portrait: { id, prompt? } } }` and nothing else. The
 * frontend never writes a portrait by URL or storage path: a portrait is only
 * writable once it has a persisted backend image id. With no id there is nothing
 * selectable to write, so this returns an empty payload (display-only portrait)
 * rather than synthesizing a URL/path write. Backend resolution is unscoped by
 * metadata UUID, so the same id resolves for `/api/characters`, campaign setup,
 * and seat assignment alike — no scope-specific variant is needed (BOU-1308).
 */
export const buildCharacterImagePayloadForWrite = (source = {}) => {
  const id = portraitImageIdForWrite(source);
  if (!id) {
    return {};
  }
  const portrait = normalizeImageReference(normalizeImageAssetBag(source).portrait || {});
  const prompt = pickFirst(portrait.prompt, source.portrait_prompt, source.portraitPrompt);
  return {
    image_assets: {
      portrait: Object.fromEntries(
        Object.entries({ id, prompt }).filter(([, value]) => value != null)
      ),
    },
  };
};

/**
 * Does this source carry a portrait at all — a writable image id or a
 * renderable URL/path (for an already-persisted character whose portrait shows
 * but predates the id migration)?
 */
export const hasPortraitImageSource = (source = {}) => {
  if (portraitImageIdForWrite(source)) {
    return true;
  }
  const portrait = normalizeImageReference(normalizeImageAssetBag(source).portrait || {});
  return Boolean(
    portrait.url || portrait.path || source.portrait_url || source.portraitUrl
    || source.portrait_path || source.portraitPath
  );
};

/**
 * Stage-cutout generation gate.
 *
 * Backend image-id resolution is unscoped (by metadata UUID), so the
 * `persistLatestProfile` PATCH that precedes stage generation no longer 400s on
 * a portrait carried by image id alone. The gate therefore enables stage
 * generation when there is a writable portrait id OR a renderable URL/path.
 */
export const hasResolvablePortraitForStage = (source = {}) => hasPortraitImageSource(source);

export const mergePortraitImageReference = (character = {}, imageRecord = {}) => {
  const portrait = normalizeImageReference(imageRecord);
  if (!isNonEmptyObject(portrait)) {
    return character;
  }
  return {
    ...character,
    image_assets: {
      ...(character.image_assets || {}),
      portrait,
    },
  };
};
